import ReactDOM from "react-dom";
import React, { useState, useEffect, useRef } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Layers, Search, Upload, Download, FileSpreadsheet, Trash2, Edit2, X, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function HierarchyPage() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [circleFilter, setCircleFilter] = useState("");
  const [divisionFilter, setDivisionFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [circles, setCircles] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [stats, setStats] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    feeder_code: "", discom_name: "PUVVNL", zone_name: "", circle_name: "",
    division_name: "", sub_division_name: "", sub_station_name: "", feeder_name: ""
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (circleFilter) params.circle_name = circleFilter;
      if (divisionFilter) params.division_name = divisionFilter;
      const [itemsRes, circlesRes, statsRes, divisionsRes] = await Promise.all([
        api.get("/hierarchy", { params }),
        api.get("/hierarchy/circles"),
        api.get("/hierarchy/stats"),
        api.get("/hierarchy/divisions", { params: circleFilter ? { circle_name: circleFilter } : {} })
      ]);
      let filteredItems = itemsRes.data.items;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredItems = filteredItems.filter(item => 
          item.feeder_code?.toLowerCase().includes(term) ||
          item.feeder_name?.toLowerCase().includes(term) ||
          item.circle_name?.toLowerCase().includes(term)
        );
      }
      setItems(filteredItems);
      setTotal(itemsRes.data.total);
      setCircles(circlesRes.data);
      setStats(statsRes.data);
      setDivisions(divisionsRes.data);
    } catch {
      toast.error("Failed to load hierarchy");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [circleFilter, divisionFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) fetchData();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.feeder_code?.trim()) { toast.error("Feeder code is required"); return; }
    if (!form.circle_name?.trim()) { toast.error("Circle name is required"); return; }
    if (!form.feeder_name?.trim()) { toast.error("Feeder name is required"); return; }
    setCreating(true);
    try {
      await api.post("/hierarchy", form);
      toast.success("Feeder added successfully");
      setShowCreate(false);
      setForm({ feeder_code: "", discom_name: "PUVVNL", zone_name: "", circle_name: "", division_name: "", sub_division_name: "", sub_station_name: "", feeder_name: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create feeder");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async (feederCode) => {
    if (!editingItem?.feeder_name?.trim()) { toast.error("Feeder name is required"); return; }
    try {
      await api.put(`/hierarchy/${feederCode}`, editingItem);
      toast.success("Feeder updated successfully");
      setEditingItem(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const handleDelete = async (feederCode) => {
    if (!window.confirm("Are you sure you want to deactivate this feeder?")) return;
    try {
      await api.delete(`/hierarchy/${feederCode}`);
      toast.success("Feeder deactivated");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    if (!window.confirm(`Are you sure you want to deactivate ${selectedItems.length} feeders?`)) return;
    try {
      await api.post("/hierarchy/bulk-delete", selectedItems);
      toast.success(`Deactivated ${selectedItems.length} feeders`);
      setSelectedItems([]);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get("/hierarchy/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `hierarchy_export_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get("/hierarchy/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "hierarchy_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Template downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await api.post("/hierarchy/import", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} errors occurred. Check console.`);
        console.log("Import errors:", response.data.errors);
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const toggleSelect = (feederCode) => {
    setSelectedItems(prev => 
      prev.includes(feederCode) 
        ? prev.filter(c => c !== feederCode)
        : [...prev, feederCode]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(i => i.feeder_code));
    }
  };

  const canManageFeeders = ["admin", "package_head", "ci"].includes(user?.role);

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="hierarchy-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>Hierarchy Management</h1>
          <p className="text-slate-500 text-sm">{total} feeders across {circles.length} circles</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={fetchData} className="p-2 border border-slate-200 rounded-sm hover:bg-slate-50" title="Refresh" data-testid="refresh-btn">
            <RefreshCw size={16} className={loading ? "animate-spin text-orange-500" : "text-slate-500"} />
          </button>
          <button onClick={handleDownloadTemplate} className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-sm text-xs font-semibold text-slate-600 hover:bg-slate-50" data-testid="download-template-btn">
            <FileSpreadsheet size={14} />Template
          </button>
          <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-2 border border-green-200 rounded-sm text-xs font-semibold text-green-600 hover:bg-green-50" data-testid="export-btn">
            <Download size={14} />Export
          </button>
          {canManageFeeders && (
            <>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileSelect} className="hidden" />
              <button 
                onClick={() => fileInputRef.current?.click()} 
                disabled={importing}
                className="flex items-center gap-1.5 px-3 py-2 border border-blue-200 rounded-sm text-xs font-semibold text-blue-600 hover:bg-blue-50 disabled:opacity-60" 
                data-testid="import-btn"
              >
                <Upload size={14} />{importing ? "Importing..." : "Import Excel"}
              </button>
              <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white rounded-sm text-xs font-bold hover:bg-orange-600" data-testid="add-feeder-btn">
                <Plus size={14} />Add Feeder
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-7 gap-3 mb-6">
        {stats.slice(0, 7).map(s => (
          <div key={s.circle_name} className="bg-white border border-slate-200 rounded-sm p-3 hover:border-orange-200 transition-colors cursor-pointer" onClick={() => setCircleFilter(circleFilter === s.circle_name ? "" : s.circle_name)}>
            <div className={`text-xs font-bold uppercase truncate ${circleFilter === s.circle_name ? "text-orange-600" : "text-slate-500"}`}>{s.circle_name}</div>
            <div className="text-xl font-black text-orange-500">{s.feeders}</div>
            <div className="text-[10px] text-slate-400">feeders · {s.divisions} divs</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
          <input 
            type="text" 
            placeholder="Search feeders..." 
            className="field-input pl-9 h-9 text-xs" 
            value={searchTerm} 
            onChange={e => setSearchTerm(e.target.value)}
            data-testid="search-input"
          />
        </div>
        <select className="field-select h-9 text-xs min-w-[150px]" value={circleFilter} onChange={e => { setCircleFilter(e.target.value); setDivisionFilter(""); }} data-testid="circle-filter">
          <option value="">All Circles</option>
          {circles.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="field-select h-9 text-xs min-w-[150px]" value={divisionFilter} onChange={e => setDivisionFilter(e.target.value)} data-testid="division-filter">
          <option value="">All Divisions</option>
          {divisions.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        {selectedItems.length > 0 && canManageFeeders && (
          <button onClick={handleBulkDelete} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white rounded-sm text-xs font-bold hover:bg-red-600" data-testid="bulk-delete-btn">
            <Trash2 size={12} />Delete {selectedItems.length}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="dt-table">
            <thead>
              <tr>
                {canManageFeeders && (
                  <th className="w-10">
                    <input type="checkbox" checked={items.length > 0 && selectedItems.length === items.length} onChange={toggleSelectAll} className="rounded" />
                  </th>
                )}
                <th>Feeder Code</th>
                <th>Feeder Name</th>
                <th>Circle</th>
                <th>Division</th>
                <th>Sub-Station</th>
                <th>Zone</th>
                <th>DISCOM</th>
                <th>Status</th>
                {canManageFeeders && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i}>{[...Array(canManageFeeders ? 10 : 9)].map((_, j) => <td key={j}><div className="h-4 bg-slate-100 rounded animate-pulse"></div></td>)}</tr>
                ))
              ) : items.length === 0 ? (
                <tr><td colSpan={canManageFeeders ? 10 : 9} className="text-center py-12 text-slate-400">
                  <Layers size={32} className="mx-auto mb-2" />
                  No feeders found
                  {searchTerm && <p className="text-xs mt-1">Try a different search term</p>}
                </td></tr>
              ) : items.map(item => (
                <tr key={item.feeder_code} className={selectedItems.includes(item.feeder_code) ? "bg-orange-50" : ""}>
                  {canManageFeeders && (
                    <td>
                      <input type="checkbox" checked={selectedItems.includes(item.feeder_code)} onChange={() => toggleSelect(item.feeder_code)} className="rounded" />
                    </td>
                  )}
                  <td>
                    <span className="font-mono text-xs font-bold text-slate-800">{item.feeder_code}</span>
                  </td>
                  <td>
                    {editingItem?.feeder_code === item.feeder_code ? (
                      <input className="field-input text-xs h-7 w-full" value={editingItem.feeder_name} onChange={e => setEditingItem({...editingItem, feeder_name: e.target.value})} />
                    ) : (
                      <span className="text-sm text-slate-700">{item.feeder_name}</span>
                    )}
                  </td>
                  <td>
                    {editingItem?.feeder_code === item.feeder_code ? (
                      <select className="field-select text-xs h-7" value={editingItem.circle_name} onChange={e => setEditingItem({...editingItem, circle_name: e.target.value})}>
                        {circles.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    ) : (
                      <span className="text-xs font-medium text-blue-600">{item.circle_name}</span>
                    )}
                  </td>
                  <td>
                    {editingItem?.feeder_code === item.feeder_code ? (
                      <input className="field-input text-xs h-7 w-full" value={editingItem.division_name} onChange={e => setEditingItem({...editingItem, division_name: e.target.value})} />
                    ) : (
                      <span className="text-xs text-slate-600">{item.division_name}</span>
                    )}
                  </td>
                  <td className="text-xs text-slate-500">{item.sub_station_name || "—"}</td>
                  <td className="text-xs text-slate-500">{item.zone_name}</td>
                  <td className="text-xs text-slate-500">{item.discom_name}</td>
                  <td>
                    <span className={`inline-flex px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${item.status === "active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                      {item.status}
                    </span>
                  </td>
                  {canManageFeeders && (
                    <td>
                      <div className="flex items-center gap-1">
                        {editingItem?.feeder_code === item.feeder_code ? (
                          <>
                            <button onClick={() => handleUpdate(item.feeder_code)} className="p-1 text-green-600 hover:bg-green-50 rounded" title="Save">
                              <Check size={14} />
                            </button>
                            <button onClick={() => setEditingItem(null)} className="p-1 text-slate-500 hover:bg-slate-50 rounded" title="Cancel">
                              <X size={14} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => setEditingItem({...item})} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Edit" data-testid={`edit-${item.feeder_code}`}>
                              <Edit2 size={14} />
                            </button>
                            <button onClick={() => handleDelete(item.feeder_code)} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Deactivate" data-testid={`delete-${item.feeder_code}`}>
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {items.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
            Showing {items.length} of {total} feeders
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-sm p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>Add New Feeder</h3>
              <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-slate-100 rounded">
                <X size={18} className="text-slate-500" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="field-label">Feeder Code *</label>
                  <input className="field-input font-mono" required placeholder="FEED-XXX-001" value={form.feeder_code} onChange={e => setForm({ ...form, feeder_code: e.target.value.toUpperCase() })} data-testid="feeder-code-input" />
                </div>
                <div className="col-span-2">
                  <label className="field-label">Feeder Name *</label>
                  <input className="field-input" required placeholder="Enter feeder name" value={form.feeder_name} onChange={e => setForm({ ...form, feeder_name: e.target.value })} data-testid="feeder-name-input" />
                </div>
                <div>
                  <label className="field-label">Circle *</label>
                  <select className="field-select" required value={form.circle_name} onChange={e => setForm({ ...form, circle_name: e.target.value })} data-testid="circle-select">
                    <option value="">Select Circle</option>
                    <optgroup label="Kashi Package">
                      <option value="Varanasi">Varanasi (VNS)</option>
                      <option value="Chandauli">Chandauli (CDU)</option>
                      <option value="Jaunpur">Jaunpur (JNP)</option>
                      <option value="Ghazipur">Ghazipur (GZP)</option>
                      <option value="Mau">Mau (MAU)</option>
                      <option value="Balia">Balia (BLA)</option>
                      <option value="Azamgarh">Azamgarh (AZM)</option>
                    </optgroup>
                    <optgroup label="Triveni Package">
                      <option value="Prayagraj">Prayagraj (PRG)</option>
                      <option value="Kaushambi">Kaushambi (KSM)</option>
                      <option value="Fatehpur">Fatehpur (FTH)</option>
                      <option value="Bhadohi">Bhadohi (BHD)</option>
                      <option value="Sonbhadra">Sonbhadra (SNB)</option>
                      <option value="Mirzapur">Mirzapur (MZP)</option>
                    </optgroup>
                    <optgroup label="Agra Package">
                      <option value="Aligarh">Aligarh (ALG)</option>
                      <option value="Mathura">Mathura (MTH)</option>
                      <option value="Firozabad">Firozabad (FZB)</option>
                      <option value="Mainpuri">Mainpuri (MNP)</option>
                      <option value="Kasganj">Kasganj (KSG)</option>
                      <option value="Etah">Etah (ETH)</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="field-label">Zone *</label>
                  <select className="field-select" required value={form.zone_name} onChange={e => setForm({ ...form, zone_name: e.target.value })} data-testid="zone-select">
                    <option value="">Select Zone</option>
                    <option value="Eastern">Eastern</option>
                    <option value="Western">Western</option>
                    <option value="Central">Central</option>
                    <option value="Northern">Northern</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="field-label">Division *</label>
                  <input className="field-input" required placeholder="Division name" value={form.division_name} onChange={e => setForm({ ...form, division_name: e.target.value })} data-testid="division-input" />
                </div>
                <div>
                  <label className="field-label">Sub-Division</label>
                  <input className="field-input" placeholder="Sub-division name" value={form.sub_division_name} onChange={e => setForm({ ...form, sub_division_name: e.target.value })} />
                </div>
                <div>
                  <label className="field-label">Sub-Station</label>
                  <input className="field-input" placeholder="Sub-station name" value={form.sub_station_name} onChange={e => setForm({ ...form, sub_station_name: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <label className="field-label">DISCOM</label>
                  <select className="field-select" value={form.discom_name} onChange={e => setForm({ ...form, discom_name: e.target.value })}>
                    <option value="PUVVNL">PUVVNL</option>
                    <option value="MVVNL">MVVNL</option>
                    <option value="PVVNL">PVVNL</option>
                    <option value="DVVNL">DVVNL</option>
                    <option value="KESCO">KESCO</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-3">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded-sm text-sm font-semibold text-slate-600 hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" disabled={creating} className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600 disabled:opacity-60" data-testid="submit-feeder-btn">
                  {creating ? "Adding..." : "Add Feeder"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
